/**
 * BlogPage - List of blog posts with AI-optimized structure
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Helmet } from 'react-helmet-async';
import SEO from '@/components/SEO';
import { BreadcrumbSchema } from '@/components/StructuredData';
import api from '@/utils/apiClient';

// Blog CollectionPage Schema for the listing page
function BlogCollectionSchema({ posts }) {
  const blogPosts = posts.map(post => ({
    "@type": "BlogPosting",
    "headline": post.title,
    "url": `https://bulaccinocafe.com/blog/${post.slug}`,
    "image": post.featured_image,
    "datePublished": post.created_at,
    "author": {
      "@type": "Organization",
      "name": post.author || "Bulaccino Cafe"
    }
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Fiji Coffee & Food Blog | Bulaccino Cafe",
    "description": "Discover the best coffee, food, and travel tips for Fiji. Expert guides on cafes, breakfast spots, and local cuisine from Bulaccino Cafe.",
    "url": "https://bulaccinocafe.com/blog",
    "mainEntity": {
      "@type": "Blog",
      "name": "Bulaccino Cafe Blog",
      "description": "Your guide to the best coffee, breakfast, and dining experiences in Fiji",
      "url": "https://bulaccinocafe.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Bulaccino Cafe",
        "url": "https://bulaccinocafe.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://bulaccinocafe.com/logo.png"
        }
      },
      "blogPost": blogPosts
    },
    "publisher": {
      "@type": "Organization",
      "name": "Bulaccino Cafe",
      "url": "https://bulaccinocafe.com"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          api.get('/blog/posts?limit=20'),
          api.get('/blog/categories')
        ]);
        
        setPosts(postsRes.data);
        setCategories(catsRes.data);
      } catch (err) {
        console.error('Failed to fetch blog data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = !searchQuery || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://bulaccinocafe.com' },
    { name: 'Blog', url: 'https://bulaccinocafe.com/blog' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="blog-page">
      <SEO 
        title="Blog - Fiji Coffee & Food Guide | Bulaccino Cafe"
        description="Discover the best coffee, food, and travel tips for Fiji. Expert guides on cafes, breakfast spots, and local cuisine from Bulaccino Cafe."
        keywords="Fiji coffee blog, best cafes Fiji, Fiji food guide, Fiji breakfast, Fiji travel tips"
        canonical="https://bulaccinocafe.com/blog"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      {posts.length > 0 && <BlogCollectionSchema posts={posts} />}

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
              Fiji Coffee & Food Blog
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Your guide to the best coffee, breakfast, and dining experiences in Fiji. 
              Local tips, travel guides, and culinary adventures.
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full"
                data-testid="blog-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-4">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedCategory 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All Posts
              </button>
              {categories.map(cat => (
                <button
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${
                    selectedCategory === cat.category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {cat.category} ({cat.count})
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts Grid */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-card rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-muted" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-6 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No articles found</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, idx) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="group bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/50 transition-all"
                >
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-post-${post.slug}`}>
                    {post.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={post.featured_image} 
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                        <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium capitalize">
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {estimateReadTime(post.content)} min read
                        </span>
                      </div>
                      <h2 className="font-heading text-xl mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="text-primary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Ready to Experience the Best Coffee in Fiji?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Visit Bulaccino Cafe for premium coffee, fresh breakfast, and warm Fijian hospitality.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/menu">
                <Button className="rounded-full" data-testid="blog-cta-menu">
                  View Our Menu
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full" data-testid="blog-cta-contact">
                  Find Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
